import { useCallback, useEffect, useState } from 'react';
import { useNotificationBar } from 'context/notificationContext';
import { useUploadFiles } from 'hooks/upload';
import { getImageSrc } from 'services/util';
import { Upload } from 'lucide-react';

interface Props {
    widget: {
        value: string | number;
        label: string;
        code: string;
    };
    initialData?: any;
    onSave: (payload: any | null) => void;
    onCancel?: () => void;
}

const VoiceWidgets = ({ widget, initialData, onSave, onCancel }: Props) => {
    const notification = useNotificationBar();
    const uploadMutation = useUploadFiles();
    const widgetData = initialData?.data || initialData;

    const [title, setTitle] = useState(widgetData?.title || widgetData?.widget_title || '');
    const [subTitle, setSubTitle] = useState(widgetData?.sub_title || widgetData?.subtitle || '');
    const [cardImageFile, setCardImageFile] = useState<File | null>(null);
    const [cardImagePreview, setCardImagePreview] = useState<string | null>(null);

    useEffect(() => {
        const cardImage = widgetData?.card_image;
        if (cardImage && !cardImageFile) {
            setCardImagePreview(getImageSrc(cardImage));
        }
    }, [widgetData, cardImageFile]);

    useEffect(() => {
        return () => {
            if (cardImagePreview?.startsWith('blob:')) {
                URL.revokeObjectURL(cardImagePreview);
            }
        };
    }, [cardImagePreview]);

    const handleCardImageSelect = useCallback((file: File | null) => {
        if (!file) {
            setCardImageFile(null);
            setCardImagePreview(null);
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            notification.push({
                message: 'File size must be under 5MB',
                type: 'error',
            });
            return;
        }

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            notification.push({
                message: 'Only JPG, JPEG, PNG, WebP, GIF allowed',
                type: 'error',
            });
            return;
        }

        setCardImageFile(file);
        setCardImagePreview(URL.createObjectURL(file));
    }, [notification]);

    const handleCardImageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        handleCardImageSelect(e.target.files?.[0] ?? null);
    }, [handleCardImageSelect]);

    const handleSave = async () => {
        if (!title || !subTitle || (!cardImageFile && !widgetData?.card_image)) {
            notification.push({
                message: 'Please fill all required fields',
                type: 'error',
            });
            return;
        }

        let uploadedCardImage = widgetData?.card_image || '';

        if (cardImageFile instanceof File) {
            try {
                const formData = new FormData();
                formData.append('file', cardImageFile);
                formData.append('customPath', 'widgets/voice');

                const res = await uploadMutation.mutateAsync({
                    data: formData,
                });
                uploadedCardImage = res?.data?.data?.key || '';
            } catch (error) {
                notification.push({
                    message: 'Failed to upload card image',
                    type: 'error',
                });
                return;
            }
        }

        const data = {
            title,
            sub_title: subTitle,
            card_image: uploadedCardImage,
            widget_title: title,
            widget_code: widget.code,
            widget_type: Number(widget.value),
            view_all: false,
            source_id: '',
            source_type: 'MANUAL',
            widget_icon: '',
            content_rule: '',
            content_type: 'STATIC',
        };

        onSave({
            name: widget.label,
            widget_code: widget.code,
            widget_type: Number(widget.value),
            data,
        });

        notification.push({
            title: 'Success',
            message: 'Voice widget saved successfully',
            type: 'success',
        });
    };

    const handleCancel = () => {
        onCancel?.();
    };

    return (
        <div className="space-y-6">
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Content Configuration</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Title <span className="text-red-500">*</span>
                        </label>
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                            placeholder="Enter title"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Sub Title <span className="text-red-500">*</span>
                        </label>
                        <input
                            value={subTitle}
                            onChange={(e) => setSubTitle(e.target.value)}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                            placeholder="Enter sub title"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Card Image <span className="text-red-500">*</span>
                        </label>
                        <div className="border-2 border-dashed rounded-lg p-3 hover:border-blue-400 transition">
                            <input
                                id="voice-card-image"
                                type="file"
                                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                                className="hidden"
                                onChange={handleCardImageChange}
                            />
                            <label htmlFor="voice-card-image" className="flex flex-col items-center justify-center cursor-pointer">
                                {cardImagePreview ? (
                                    <img
                                        src={cardImagePreview}
                                        alt="Card preview"
                                        className="w-full h-32 object-cover rounded border"
                                    />
                                ) : (
                                    <>
                                        <Upload
                                            className="text-gray-400 mb-2"
                                            size={25}
                                        />
                                        <p className="text-sm text-gray-500">
                                            Click to upload card image
                                        </p>
                                        <p className="text-xs text-gray-400">
                                            JPG / PNG / WebP / GIF - Max 5MB
                                        </p>
                                    </>
                                )}
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-200 dark:border-slate-600">
                <button
                    onClick={handleCancel}
                    className="px-6 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Save
                </button>
            </div>
        </div>
    );
};

export default VoiceWidgets;
